
import { useState, useEffect } from 'react';
import { Comment } from '../../types/comment';
import { generateAIResponses } from '../../services/aiGenerationService';
import { Loader2, Copy, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface AIGeneratedResponsesProps {
  comment: Comment;
  onSelectResponse: (response: string) => void;
}

const AIGeneratedResponses = ({ comment, onSelectResponse }: AIGeneratedResponsesProps) => {
  const [responses, setResponses] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRegenerating, setIsRegenerating] = useState(false);

  useEffect(() => {
    loadResponses();
  }, [comment.id]);

  const loadResponses = async () => {
    setIsLoading(true);
    try {
      const generatedResponses = await generateAIResponses(comment);
      setResponses(generatedResponses);
    } catch (error) {
      console.error('Erreur lors de la génération des réponses IA:', error);
      setResponses([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegenerate = async () => {
    setIsRegenerating(true);
    try {
      const newResponses = await generateAIResponses(comment);
      setResponses(newResponses);
    } catch (error) {
      console.error('Erreur lors de la régénération des réponses:', error);
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleSelectResponse = (response: string) => {
    onSelectResponse(response);
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-custom-primary dark:text-custom-secondaryLight mb-4" />
        <p className="text-gray-600 dark:text-gray-400">Génération de réponses IA en cours...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Réponses générées par l'IA</h3>
        <Button
          variant="outline"
          size="sm"
          onClick={handleRegenerate}
          disabled={isRegenerating}
          className="text-gray-600 dark:text-gray-300 dark:border-gray-700 dark:hover:bg-gray-800/80"
        >
          {isRegenerating ? (
            <>
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              Régénération...
            </>
          ) : (
            <>
              <RefreshCw className="h-4 w-4 mr-1" />
              Régénérer
            </>
          )}
        </Button>
      </div>

      {responses.length === 0 ? (
        <div className="text-center py-6 bg-gray-50 dark:bg-gray-800/30 rounded-lg">
          <p className="text-gray-500 dark:text-gray-400">Aucune réponse générée</p>
        </div>
      ) : (
        responses.map((response, index) => (
          <div
            key={index}
            className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800/40 hover:bg-gray-50 dark:hover:bg-gray-800/80 transition-colors"
          >
            <p className="text-gray-800 dark:text-gray-200 mb-3">{response}</p>
            <div className="flex justify-end space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigator.clipboard.writeText(response)}
                className="text-gray-600 dark:text-gray-400 dark:border-gray-700 dark:hover:bg-gray-800"
              >
                <Copy className="h-4 w-4 mr-1" />
                Copier
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={() => handleSelectResponse(response)}
                className="bg-custom-primary hover:bg-custom-dark text-white dark:bg-custom-secondaryLight dark:text-gray-900 dark:hover:bg-custom-secondaryLight/90"
              >
                Utiliser
              </Button>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default AIGeneratedResponses;
